import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { format } from 'date-fns';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import SuccessModal from './SuccessModal';
import LoadingImpulse from './LoadingImpulse'; 

const NOTIFICATION_TYPES = {
  deposit: { icon: 'arrow_downward', color: 'text-green-400', bg: 'bg-green-100', title: 'New Deposit' },
  completed: { icon: 'check_circle', color: 'text-green-700', bg: 'bg-green-100', title: 'Completed' },
  withdrawal: { icon: 'arrow_upward', color: 'text-purple-400', bg: 'bg-purple-100', title: 'Withdrawal' },
  failed: { icon: 'error', color: 'text-red-400', bg: 'bg-red-100', title: 'Failed' },
  pending: { icon: 'schedule', color: 'text-yellow-400', bg: 'bg-yellow-100', title: 'Pending' },
  default: { icon: 'notifications', color: 'text-gray-600', bg: 'bg-gray-100', title: 'System' }
};

function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);

  // Modal states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [deleteConfig, setDeleteConfig] = useState({
    title: '',
    message: '',
    onConfirm: () => {}
  });

  const audioRef = useRef(null);
  const socketRef = useRef(null);
  const audioUnlocked = useRef(false);

  const limit = 20;
  const STORAGE_KEY = 'instant_transfer_audio_unlocked';

  useEffect(() => {
    const unlocked = localStorage.getItem(STORAGE_KEY) === 'true';
    if (unlocked) audioUnlocked.current = true;
  }, []);

  const playSound = () => {
    if (!audioRef.current || !audioUnlocked.current) return;
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(() => {});
  };

  const unlockAudio = () => {
    if (audioUnlocked.current) return;
    const silent = new Audio('data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=');
    silent.play().then(() => {
      audioUnlocked.current = true;
      localStorage.setItem(STORAGE_KEY, 'true');
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    });
  };

  const fetchNotifications = async (pageNum = 1) => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `https://sys.instant-m-transfer.com/api/admin/notifications?page=${pageNum}&limit=${limit}`
      );
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unreadCount || 0);
      setTotalPages(Math.ceil(res.data.pagination.total / limit) || 1);
      setPage(pageNum);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load notifications');
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.post(
        `https://sys.instant-m-transfer.com/api/admin/notifications/${id}/read`,
        {}
      );
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, adminRead: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(
        'https://sys.instant-m-transfer.com/api/admin/notifications/read-all',
        {}
      );
      setNotifications(prev => prev.map(n => ({ ...n, adminRead: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const showDeleteModal = (title, message, onConfirm) => {
    setDeleteConfig({ title, message, onConfirm });
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  const showSuccessModal = (message) => {
    setSuccessMessage(message);
    setSuccessModalOpen(true);
  };

  const closeSuccessModal = () => {
    setSuccessModalOpen(false);
  };

  const deleteNotification = (id) => {
    showDeleteModal(
      'Delete Notification',
      'Are you sure you want to delete this notification?',
      async () => {
        try {
          await axios.delete(
            `https://sys.instant-m-transfer.com/api/admin/notifications/${id}`
          );
          const notif = notifications.find(n => n.id === id);
          setNotifications(prev => prev.filter(n => n.id !== id));
          if (notif && !notif.adminRead) setUnreadCount(c => Math.max(0, c - 1));
          showSuccessModal('Notification deleted successfully.');
        } catch (err) {
          console.error('Delete error:', err);
          alert('Failed to delete.');
        } finally {
          closeDeleteModal();
        }
      }
    );
  };

  const deleteAllRead = () => {
    showDeleteModal(
      'Delete All Read Notifications',
      'Are you sure you want to delete all read notifications?',
      async () => {
        setIsDeleting(true);
        try {
          const { data } = await axios.delete(
            'https://sys.instant-m-transfer.com/api/admin/notifications/read-all'
          );
          setNotifications(prev => prev.filter(n => !n.adminRead));
          showSuccessModal(`${data.deletedCount} read notification(s) deleted successfully.`);
        } catch (err) {
          console.error(err);
          alert('Failed to delete read notifications.');
        } finally {
          setIsDeleting(false);
          closeDeleteModal();
        }
      }
    );
  };

  const bulkDelete = () => {
    if (selectedIds.size === 0) return;
    showDeleteModal(
      'Delete Selected Notifications',
      `Are you sure you want to delete ${selectedIds.size} selected notification(s)?`,
      async () => {
        setIsDeleting(true);
        try {
          const { data } = await axios.delete(
            'https://sys.instant-m-transfer.com/api/admin/notifications/bulk',
            { data: { ids: Array.from(selectedIds) } }
          );
          setNotifications(prev => prev.filter(n => !selectedIds.has(n.id)));
          const deletedUnread = notifications.filter(n => selectedIds.has(n.id) && !n.adminRead).length;
          setUnreadCount(c => Math.max(0, c - deletedUnread));
          showSuccessModal(`${data.deletedCount} notification(s) deleted successfully.`);
          setSelectedIds(new Set());
          setSelectMode(false);
        } catch (err) {
          console.error('Bulk delete error:', err);
          alert('Failed to delete selected notifications.');
        } finally {
          setIsDeleting(false);
          closeDeleteModal();
        }
      }
    );
  };

  const toggleSelect = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedIds(newSet);
  };

  const selectAllOnPage = () => {
    const pageIds = notifications.map(n => n.id);
    const newSet = new Set(selectedIds);
    pageIds.forEach(id => newSet.add(id));
    setSelectedIds(newSet);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  useEffect(() => {
    if (!audioUnlocked.current) {
      document.addEventListener('click', unlockAudio);
      document.addEventListener('touchstart', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    }

    const socket = io('https://sys.instant-m-transfer.com', {
      transports: ['websocket'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socket.on('connect', () => socket.emit('joinAdmin'));
    socket.on('newNotification', (notif) => {
      playSound();
      setNotifications(prev => [notif, ...prev]);
      setUnreadCount(prev => prev + 1);
    });

    socketRef.current = socket;

    return () => {
      if (!audioUnlocked.current) {
        document.removeEventListener('click', unlockAudio);
        document.removeEventListener('touchstart', unlockAudio);
        document.removeEventListener('keydown', unlockAudio);
      }
      socket.close();
    };
  }, []);

  useEffect(() => {
    fetchNotifications(1);
  }, []);

  const hasReadNotifications = notifications.some(n => n.adminRead);
  const selectedCount = selectedIds.size;

  return (
    <div className="min-h-screen bg-gray-100 py-2 px-1 sm:py-4 sm:px-2 md:py-6 md:px-4">
      <audio ref={audioRef} src="/assets/notification.mp3" preload="auto" />

      <div className="max-w-full mx-auto sm:max-w-3xl md:max-w-6xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
          <div className="flex items-center gap-2">
            <span className="material-icons text-purple-400">notifications</span>
            <h2 className="text-base font-bold text-gray-800 sm:text-xl md:text-2xl font-['Inter']">
              Notifications
            </h2>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium text-white bg-red-500 rounded-full animate-pulse">
                {unreadCount} new
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                setSelectMode(!selectMode);
                if (!selectMode) clearSelection();
              }}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                selectMode
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {selectMode ? 'Cancel' : 'Select'}
            </button>

            {selectMode && selectedCount > 0 && (
              <button
                onClick={bulkDelete}
                disabled={isDeleting}
                className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs font-medium disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : `Delete ${selectedCount}`}
              </button>
            )}

            {selectMode && notifications.length > 0 && (
              <button
                onClick={selectAllOnPage}
                className="px-3 py-1 bg-gray-700 text-white rounded-lg hover:bg-gray-800 text-xs font-medium "
              >
                Select Page
              </button>
            )}

            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="px-3 py-1 bg-green-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 text-xs font-medium"
              >
                Mark all as read
              </button>
            )}

            {hasReadNotifications && (
              <button
                onClick={deleteAllRead}
                disabled={isDeleting}
                className="px-3 py-1 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 text-xs font-medium disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete read'}
              </button>
            )}
          </div>
        </div>

        {!audioUnlocked.current && (
          <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 px-3 py-2 rounded-lg mb-3 text-xs font-medium">
            <span className="material-icons text-sm align-middle mr-1">volume_up</span>
            Click anywhere to enable notification sounds (once)
          </div>
        )}

        {error && (
          <div className="bg-red-50 border-l-2 border-red-500 p-3 rounded-r shadow text-red-700 text-xs font-medium mb-3">
            {error}
          </div>
        )}

        {/* ← ONLY THIS LOADING BLOCK REPLACED */}
        {isLoading && page === 1 && (
          <div className="flex justify-center py-12">
            <LoadingImpulse size="lg" color="blue" />
          </div>
        )}

        <div className="space-y-2">
          {notifications.length === 0 && !isLoading ? (
            <div className="text-center py-12 text-gray-500">
              <span className="material-icons text-6xl text-gray-300 mb-2">notifications_off</span>
              <p className="text-sm italic">No notifications yet</p>
            </div>
          ) : (
            notifications.map((notif) => {
              const type = NOTIFICATION_TYPES[notif.type] || NOTIFICATION_TYPES.default;
              const isRead = notif.adminRead;
              const isSelected = selectedIds.has(notif.id);

              return (
                <div
                  key={notif.id}
                  className={`p-4 rounded-lg border transition-all duration-200 relative flex items-start gap-3 ${
                    isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                  } ${
                    !isRead
                      ? 'bg-white border-blue-200 shadow-sm'
                      : 'bg-gray-50 border-gray-200'
                  } ${selectMode ? 'pl-12' : ''}`}
                >
                  {selectMode && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(notif.id)}
                      className="absolute top-4 left-4 h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                  )}

                  <div className={`p-2 rounded-lg ${type.bg}`}>
                    <span className={`material-icons text-lg ${type.color}`}>{type.icon}</span>
                  </div>

                  <div className="flex-1 pr-16">
                    <div className="flex justify-between items-center mb-1">
                      <h3 className="font-semibold text-gray-900 text-sm">
                        {type.title}
                      </h3>
                      <span className="text-xs text-gray-500">
                        {format(new Date(notif.timestamp), 'MMM d, h:mm a')}
                      </span>
                    </div>
                    <div
                      className="text-sm text-gray-700 mb-2"
                      dangerouslySetInnerHTML={{ __html: notif.message }}
                    />
                    {(notif.data?.accountReference || notif.data?.phoneNumber) && (
                      <div className="text-xs text-gray-500 font-mono mb-2">
                        {notif.data.accountReference}
                        {notif.data.phoneNumber && ` • ${notif.data.phoneNumber}`}
                      </div>
                    )}
                    {!isRead && (
                      <button
                        onClick={() => markAsRead(notif.id)}
                        className="text-xs font-bold text-green-700 hover:underline"
                      >
                        Mark as read
                      </button>
                    )}
                  </div>

                  {isRead && (
                    <div className="absolute bottom-10 right-20 flex items-center gap-1 text-green-700 text-xs font-medium">
                      <span className="material-icons text-sm">check_circle</span>
                      <span>Read</span>
                    </div>
                  )}

                  <button
                    onClick={() => deleteNotification(notif.id)}
                    className="absolute top-4 right-4 text-green-700 hover:text-green-700 transition-colors"
                    title="Delete"
                  >
                    <span className="material-icons text-lg">delete</span>
                  </button>
                </div>
              );
            })
          )}
        </div>

        {totalPages > 1 && (
          <div className="mt-6 flex justify-center gap-2">
            <button
              onClick={() => fetchNotifications(Math.max(1, page - 1))}
              disabled={page === 1 || isLoading}
              className="px-3 py-1 bg-gray-800 text-white rounded hover:bg-gray-700 disabled:opacity-50 text-xs font-medium"
            >
              Previous
            </button>
            <span className="text-xs text-gray-600 self-center">
              Page <strong>{page}</strong> of <strong>{totalPages}</strong>
            </span>
            <button
              onClick={() => fetchNotifications(page + 1)}
              disabled={page === totalPages || isLoading}
              className="px-3 py-1 bg-gray-800 text-white rounded hover:bg-gray-700 disabled:opacity-50 text-xs font-medium"
            >
              Next
            </button>
          </div>
        )}
      </div>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={deleteConfig.onConfirm}
        title={deleteConfig.title}
        message={deleteConfig.message}
      />

      <SuccessModal
        isOpen={successModalOpen}
        onClose={closeSuccessModal}
        message={successMessage}
      />

      {/* Impulse Animation CSS */}
      <style jsx global>{`
        @keyframes impulse {
          0%, 100% { transform: scaleY(0.4); opacity: 0.5; }
          50% { transform: scaleY(1); opacity: 1; }
        }
        .animate-impulse {
          animation: impulse 1.2s infinite ease-in-out;
          transform-origin: bottom;
        }
      `}</style>

      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600&family=Open+Sans:wght@400&display=swap');
        .material-icons {
          font-size: 20px;
          display: inline-flex;
          align-items: center;
        }
      `}</style>
    </div>
  );
}

export default Notifications;